import _merge from 'lodash/merge';
import querystring from 'query-string';

import { MOCK_API_ENDPOINT, FEVER_API_ENDPOINT, FEVER_HOST } from 'config/envVariable';
import { getFirebaseIdToken, signout } from 'features/login/utils/firebaseManager';
import { generateLoginUrl } from 'utils/login';
import {
    toPermissionNotDeniedPage,
    paths,
    toOTPVerifyPage
} from 'features/login/hooks/useInitAndRedirectApp';
import { history } from 'index';
import { generateApiErrObj } from 'features/webhooks/useApiErrorHandler';
import { isJSONParsable } from 'utils/objectManipulate';
import { ERROR_MESSAGES, ERROR_CODES } from 'config/apiErrorCodes';
import { permissionDeniedReasons } from 'config/permissionDeniedReasons';
import {
    isRoleAuthoritiesFailedError,
    isRoleNoPermissionError,
    isOtpValidationFailedError,
    isTurnstileVerificationFailedResp
} from 'utils/errorObjectChecker';

export const methods = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    PATCH: 'PATCH',
    DELETE: 'DELETE'
};

export const IMG_UPLOAD_API_PATH = '/services/images';

export async function fetchOpts(method, body = null, setting = {}) {
    return _merge(
        {},
        {
            method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${(await getFirebaseIdToken()) || ''}`
            }
        },
        body ? { body: JSON.stringify(body) } : {},
        setting
    );
}

export function getRedirectUrlWithFallbackSponsor() {
    // 移除 refUrl 的 sponsorId，並作為新 url 回傳
    const { origin, pathname, search } = window.location;
    const searchObj = querystring.parse(search);
    if (searchObj?.refUrl) {
        const refUrl = new URL(searchObj?.refUrl);
        const params = new URLSearchParams(refUrl.search);
        params.delete('sponsorId');
        refUrl.search = params;
        return refUrl.toString();
    } else if (pathname === paths.PERMISSION_DENIED) {
        return null;
    } else {
        delete searchObj.sponsorId;
        const qs = querystring.stringify(searchObj);

        return `${origin}${pathname}${Boolean(qs) ? `?${qs}` : ``}`;
    }
}

export { ERROR_MESSAGES, ERROR_CODES };

export async function redirectIfInvalid(resp) {
    try {
        // Check if response is valid before processing
        if (!resp) {
            console.error('redirectIfInvalid: Response is undefined or null');
            return resp;
        }

        // Clone the response to avoid consuming the body stream
        // Response body can only be read once. Since normalizeResponse() will be called again
        // in the main flow, we need to clone the response here to read the payload for logging
        const clonedResp = resp.clone();
        const response = await normalizeResponse(clonedResp);
        const { httpStatus, payload } = response;

        // Early return for non-error status codes
        if (httpStatus < 400) return resp;

        // Handle 401 - Unauthorized
        if (httpStatus === 401) {
            await signout();
            window.location.href = generateLoginUrl();
            return resp;
        }

        // Handle 403 - Forbidden with specific error codes
        if (httpStatus === 403) {
            if (isRoleAuthoritiesFailedError(response)) {
                // 此組合（僅 fever-api 會回）代表 admin 在該 sponsor 查無角色，
                // 與 promotion-tool 同 code 不同義的 403 區分開，故 message + code 雙重比對不可放寬
                toPermissionNotDeniedPage(history, permissionDeniedReasons.SPONSOR_NOT_FOUND);
                return resp;
            } else if (isRoleNoPermissionError(response)) {
                toPermissionNotDeniedPage(history, permissionDeniedReasons.ROLE_NO_PERMISSION);
                return resp;
            } else if (isOtpValidationFailedError(response)) {
                toOTPVerifyPage(history);
                return resp;
            }
        }

        if (
            payload?.message === ERROR_MESSAGES.TOKEN_REQUIRED || // others api (ex: zines api, promo api...etc)
            payload?.errors?.message === ERROR_MESSAGES.TOKEN_REQUIRED // form api
        ) {
            await signout();
            window.location.href = generateLoginUrl();
        }

        return resp;
    } catch (error) {
        console.error('Redirect if invalid', error);
        return resp;
    }
}

export function fetchWith(origin) {
    return async (path, optionsPromise, { redirect = true } = {}) => {
        const options = await optionsPromise;
        // SSRF 誤報：origin 由 fetchWith() 的呼叫端以 config/envVariable 常數傳入，非使用者輸入。
        // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
        return fetch(`${origin}${path}`, options).then(res =>
            redirect ? redirectIfInvalid(res) : res
        );
    };
}

export async function feverApiFetchOpts({
    method = methods.GET,
    headers = {},
    body = null,
    formData,
    turnstileToken, // Cloudflare Turnstile token；有值時以 X-Cfts-Token header 帶入（含 formData 情境）
    apiVersion = 1,
    feverHost,
    credentials = 'include', // 若是 /services 底下的 api，則需要設定 credentials 為 include，因為有 OTP 驗證需求
    ...rest
} = {}) {
    const firebaseToken = await getFirebaseIdToken();
    return {
        method,
        headers: new Headers({
            'Fever-Host': feverHost || FEVER_HOST,
            Accept: `application/x.api.v${apiVersion}+json`,
            ...(firebaseToken ? { Authorization: `Bearer ${firebaseToken}` } : {}),
            ...(formData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
            ...(turnstileToken ? { 'X-Cfts-Token': turnstileToken } : {}),
            ...headers
        }),
        body: formData || (body && JSON.stringify(body)),
        credentials,
        ...rest
    };
}

export function normalizeResponse(resp) {
    if (!resp) {
        console.error('normalizeResponse: Response is undefined or null');
        return Promise.reject(new Error('Response is undefined or null'));
    }

    if (typeof resp.json !== 'function') {
        console.error('normalizeResponse: Response does not have json method', resp);
        return Promise.reject(new Error('Invalid response object'));
    }

    return resp.json().then(payload => ({ httpStatus: resp.status, payload }));
}

export async function fetches(
    path,
    optionsPromise,
    {
        dataKeys = 'data',
        isMock = false,
        strictErrThrow = true,
        passResponse = false,
        throwErrConfigs,
        redirect = true
    } = {}
) {
    const options = await optionsPromise;
    const baseUrl = isMock ? MOCK_API_ENDPOINT : FEVER_API_ENDPOINT;
    // SSRF 誤報：baseUrl 來自建置期常數（MOCK_API_ENDPOINT / FEVER_API_ENDPOINT），非使用者可控。
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
    return fetch(`${baseUrl}${path}`, options)
        .then(res => (redirect ? redirectIfInvalid(res) : res))
        .then(normalizeResponse)
        .then(resp => {
            // passResponse: return the full normalized response `{ httpStatus, payload }`
            // WITHOUT unwrapping to `.data` and WITHOUT throwing on 4xx. For callers that need
            // the complete backend envelope (`{ code, message, data }`) and handle status
            // themselves — e.g. the Media Manager fetcher, whose factory reads outer `code`/`message`.
            if (passResponse) {
                return resp;
            }

            if (resp.httpStatus >= 400) {
                // Turnstile 驗證失敗一律拋出可辨識的原始 resp，讓呼叫端（useTurnstile）能 reset 重試 1 次。
                // 放在 throwErrConfigs 之前，避免帶 throwErrConfigs 的 API 因無對應 checker 而把 turnstile 失敗吞掉。
                if (isTurnstileVerificationFailedResp(resp)) throw resp;
                if (throwErrConfigs) throwError(resp, throwErrConfigs, options.body);
                else throw resp;
            }

            if (!strictErrThrow) {
                return { ...resp, payload: resp.payload[dataKeys] };
            }

            return resp.payload[dataKeys];
        });
}

export async function fetchDownload(path, optionsPromise) {
    const options = await optionsPromise;
    // SSRF 誤報：此為瀏覽器端 fetch，baseURL 為建置期常數 FEVER_API_ENDPOINT，
    // path 由前端程式碼指定；瀏覽器無法藉此存取後端內網。
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
    return fetch(`${FEVER_API_ENDPOINT}${path}`, options).then(resp => {
        if (resp.status !== 200) {
            throw resp;
        }
        return resp.blob();
    });
}

export function throwError(resp, throwErrConfigs = [], body = '') {
    const configs = Object.entries(throwErrConfigs);
    for (const [errType, checker] of configs) {
        if (typeof checker === 'function' && checker(resp)) {
            const apiBody = isJSONParsable(body) ? JSON.parse(body) : body;
            throw generateApiErrObj(resp, errType, apiBody);
        }
    }
}
